/** Copyright © 2024 Qcells. All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized use,
 * duplication, or distribution of software is strictly prohibited.
 */
import isEmpty from 'lodash-es/isEmpty';
import uniq from 'lodash-es/uniq';

import { SORT_ORDER } from '@hems/util/src/constant/constant';
import type { ErrorLevelValue, ErrorTypeValue } from '@hems/util/src/constant/eventHistory';
import {
  ERROR_LEVEL,
  ERROR_PREFIX,
  ERROR_STATUS_TYPE,
  ERROR_RESOLVED_FILTER,
  ERROR_TYPE,
} from '@hems/util/src/constant/eventHistory';
import {
  defaultPeriod,
  defaultStartDateTimeOptions,
  extractDateWithoutOffset,
  getDateWithAdjustedTime,
  getLocalDate,
} from '@hems/util/src/helper/dateHelper';
import { addSortOrderMark, isEmptyArray, isNull } from '@hems/util/src/helper/helper';
import { getStringValue, isErrorLevelType } from '@hems/util/src/helper/tsguardHelper';

import type { i18nTranslation } from 'hems';

import type {
  DashboardErrorInfo,
  DisplayedSiteErrorInfo,
  ErrorCodeDisplayDetails,
  ErrorCodeInfo,
  ErrorHistoryInfo,
  ErrorHistoryParams,
  RecentErrorHistoryInfo,
  SiteErrorInfo,
} from 'hems/event';

type ErrorList = { data: ErrorHistoryInfo[]; totalCount: number };

const dateFormatConfig = { isTime: true, needUtcConvert: true };

export const getErrorHistoryParams = (condition: ErrorHistoryParams): ErrorHistoryParams => {
  const {
    errorType,
    pageNumber,
    pageSize,
    sort,
    search,
    date = defaultPeriod,
    errorLevel,
    excludeErrorCodes,
    errorResolved,
  } = condition;

  const commonParams = {
    errorType,
    pageNumber,
    pageSize,
    sort: getStringValue(sort),
    search: getStringValue(search),
    cleared: errorResolved === ERROR_RESOLVED_FILTER.RESOLVED,
    errorLevel: errorLevel?.toUpperCase() as ErrorLevelValue,
    excludeErrorCodes: getStringValue(excludeErrorCodes),
  };

  if (errorResolved === ERROR_RESOLVED_FILTER.CURRENT) {
    return commonParams;
  }

  return {
    ...commonParams,
    clearDateStart: getDateWithAdjustedTime(date?.start, defaultStartDateTimeOptions).toISOString(),
  };
};

const getDeviceId = (deviceId: string, subDeviceId?: string | null) => {
  if (isNull(subDeviceId)) {
    return deviceId;
  }

  return `${deviceId}(${subDeviceId})`;
};

const errorLevelMap: Record<ErrorLevelValue, string> = {
  [ERROR_LEVEL.FAULT]: 'device.fault',
  [ERROR_LEVEL.WARNING]: 'device.warning',
  [ERROR_LEVEL.ALARM]: 'common.alarm',
};

const getErrorLevel = (errorLevel: string | null, t: i18nTranslation): string | null =>
  isErrorLevelType(errorLevel) ? t(errorLevelMap[errorLevel]) : errorLevel;

const getDescription = (display: ErrorCodeDisplayDetails): string => {
  return getStringValue(display.description) ?? '';
};

export const getErrorData = (errorList: ErrorHistoryInfo[], totalCount: number, t: i18nTranslation): ErrorList => {
  return {
    data: errorList.map((errorInfo) => ({
      ...errorInfo,
      deviceId: getDeviceId(errorInfo.deviceId, errorInfo.subDeviceId),
      triggerDate: errorInfo.triggerDate
        ? getLocalDate(extractDateWithoutOffset(errorInfo.triggerDate), dateFormatConfig)
        : '',
      clearDate: errorInfo.clearDate
        ? getLocalDate(extractDateWithoutOffset(errorInfo.clearDate), dateFormatConfig)
        : '',
      triggerDateUtc: errorInfo.triggerDate ?? null,
      clearDateUtc: errorInfo.clearDate ?? null,
      errorLevel: getErrorLevel(errorInfo.errorLevel, t),
      description: getDescription(errorInfo.display),
    })),
    totalCount,
  };
};

export const getRecentErrorData = (errorList: ErrorHistoryInfo[], t: i18nTranslation): RecentErrorHistoryInfo[] => {
  return errorList.map((error) => ({
    siteId: error.siteId,
    deviceId: getDeviceId(error.deviceId, error.subDeviceId),
    errorLevel: getErrorLevel(error.errorLevel, t),
    triggerDate: getLocalDate(extractDateWithoutOffset(error.triggerDate), dateFormatConfig),
    errorCode: error.errorCode,
    // TODO: 기존의 recent event history에서 서버에서 주는 값이 항상 'Y'인지 확인 필요
    newFlag: 'Y',
  }));
};

export const getSiteErrorParams = (siteId: string, errorType: ErrorTypeValue) => {
  return {
    sort: addSortOrderMark('triggerDate', SORT_ORDER.DESCENDING),
    errorType,
    cleared: false,
    search: siteId,
  };
};

const getErrorInfoByPrefix = (errors: ErrorHistoryInfo[], prefix: string): ErrorHistoryInfo[] => {
  return errors.filter((error) => error.errorCode.startsWith(prefix));
};

export const getSiteErrorStatusType = (errors: ErrorHistoryInfo[]) => {
  const faults = errors.some((error) => error.errorLevel === ERROR_LEVEL.FAULT);
  const warnings = errors.some((error) => error.errorLevel === ERROR_LEVEL.WARNING);
  if (faults) {
    return ERROR_STATUS_TYPE.ERROR;
  }
  if (warnings) {
    return ERROR_STATUS_TYPE.WARNING;
  }

  return null;
};

export const getSiteErrorData = (errorList: ErrorHistoryInfo[]): SiteErrorInfo => {
  const errorCodeAndDate = errorList.map((error) => ({
    ...error,
    triggerDate: getLocalDate(extractDateWithoutOffset(error.triggerDate), dateFormatConfig),
    triggerDateUtc: error.triggerDate ?? null,
  }));
  const siteErrors: SiteErrorInfo = {};
  Object.values(ERROR_PREFIX).forEach((prefix) => {
    const errorsByPrefix = getErrorInfoByPrefix(errorCodeAndDate, prefix);
    siteErrors[prefix] = { errorStatusType: getSiteErrorStatusType(errorsByPrefix), errorInfo: errorsByPrefix };
  });

  return siteErrors;
};

const getDeviceIdFilter = (errors: DashboardErrorInfo | null, deviceId?: string) => {
  let errorInfos = errors?.errorInfo;

  if (deviceId) {
    errorInfos = errors?.errorInfo?.filter((error) => error.deviceId === deviceId);
  }

  return {
    errorStatusType: getSiteErrorStatusType(errorInfos ?? []),
    errorInfo: errorInfos ?? [],
  };
};

export const getErrorCodeAndDateForAC = (
  errors?: DashboardErrorInfo | null,
  deviceId?: string
): DisplayedSiteErrorInfo[] => {
  if (!errors || isEmpty(errors.errorInfo)) {
    return [];
  }

  const errorCodeAndDate = uniq(
    getDeviceIdFilter(errors, deviceId).errorInfo?.map((error) => ({
      errorCode: error.errorCode,
      triggerDate: error.triggerDate,
    }))
  );

  return errorCodeAndDate;
};

export const getErrorCodeAndDateForACFromArray = (
  errorArray: (DashboardErrorInfo | null)[],
  deviceId?: string
): DisplayedSiteErrorInfo[] => {
  const isErrorCodesEmpty = errorArray.every((item) => item?.errorInfo?.length === 0);
  if (isErrorCodesEmpty) {
    return [];
  }

  const errors = errorArray
    .map((item) => getErrorCodeAndDateForAC(item, deviceId))
    .filter((item) => !isEmptyArray(item));

  return errors.flat();
};

export const getErrorStatusTypeFromArray = (errorArray: (DashboardErrorInfo | null)[], deviceId?: string) => {
  const isErrorCodesEmpty = errorArray.every((item) => item?.errorInfo?.length === 0);
  if (isErrorCodesEmpty) {
    return;
  }

  const errorStatusTypeByArray = errorArray.map((item) => getDeviceIdFilter(item, deviceId).errorStatusType);

  if (errorStatusTypeByArray.includes(ERROR_STATUS_TYPE.ERROR)) {
    return ERROR_STATUS_TYPE.ERROR;
  }
  if (errorStatusTypeByArray.includes(ERROR_STATUS_TYPE.WARNING)) {
    return ERROR_STATUS_TYPE.WARNING;
  }

  return;
};

export const getAllErrorCodeList = (errorCodeInfoList: ErrorCodeInfo[], errorType: ErrorTypeValue) => {
  if (errorType === ERROR_TYPE.MAJOR) {
    return uniq(errorCodeInfoList.map((errorCode) => `${errorCode.major}`)).sort();
  }

  return errorCodeInfoList
    .map((errorCode) => `${errorCode.major}${errorCode.minor}${errorCode.level.charAt(0)}`)
    .sort();
};
